import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Typography, Space } from 'antd';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  lanzarMotor,
  getCicloNuevo,
  getProgreso,
  type ReconciliacionProgreso,
} from '../../api/reconciliacionApi';
import { FctIcono, FctAviso } from '../../fct/react';

const { Text, Title } = Typography;

interface MotorProgressOverlayProps {
  visible: boolean;
  modo: string;
  opcion?: string;
  onClose: () => void;
  onCompleted: () => void;
}

type OverlayState = 'launching' | 'polling' | 'completed' | 'error' | 'launch_failed' | 'huerfano';

// Tras el 202, la cabecera del ciclo aparece en 4-7 s, o en casi un minuto tras un reinicio de la Function App:
// se busca cada 2 s durante 120 s (§4.2 de la referencia del motor, #150).
export const INTERVALO_SONDEO_MS = 2000;
export const ESPERA_CICLO_NUEVO_MS = 120000;
export const MENSAJE_NO_ARRANCO = 'El motor no ha arrancado. Avisa a Sistemas.';
export const MENSAJE_HUERFANO = 'Hay una reconciliación que no terminó correctamente. Avisa a Sistemas.';

// Mapeo de fases del motor a líneas UI (#150; §4.3 de la referencia del motor).
// Las fases 0-2 se agrupan en "Lectura de datos"
// Fase 3 → Farmacias, 4 → Farmacéuticos, 5 → Relaciones, 6 → Traslados, 8 → Escritura. La 7 no se usa.
// Los totales llegan todos al entrar en la fase 6, así que no se muestran antes.
interface FaseUI {
  key: string;
  label: string;
  completedWhenFase: number; // se marca ✓ cuando fase >= este valor
  getDetail?: (p: ReconciliacionProgreso) => string | null;
  hasProgress?: boolean; // barra de progreso (solo fase 5)
}

const FASE_TOTALES = 6;

const FASES_UI: FaseUI[] = [
  {
    key: 'lectura',
    label: 'Lectura de datos',
    completedWhenFase: 3,
  },
  {
    key: 'farmacias',
    label: 'Farmacias',
    completedWhenFase: 4,
    getDetail: (p) => {
      if (p.fase < FASE_TOTALES) return null;
      const parts = [];
      if (p.totalFarmaciasAltas > 0) parts.push(`${p.totalFarmaciasAltas} altas`);
      if (p.totalFarmaciasBajas > 0) parts.push(`${p.totalFarmaciasBajas} bajas`);
      return parts.length > 0 ? parts.join(', ') : 'sin cambios';
    },
  },
  {
    key: 'farmaceuticos',
    label: 'Farmacéuticos',
    completedWhenFase: 5,
    getDetail: (p) => {
      if (p.fase < FASE_TOTALES) return null;
      return p.totalFarmaceuticos > 0 ? `${p.totalFarmaceuticos} sin vincular` : 'sin cambios';
    },
  },
  {
    key: 'relaciones',
    label: 'Relaciones',
    completedWhenFase: 6,
    hasProgress: true,
    getDetail: (p) => {
      const omitidas = p.farmaciasOmitidas ?? 0;
      const textoOmitidas = omitidas > 0 ? `${omitidas} omitidas` : null;
      if (p.fase === 5 && p.farmaciasAnalizadas > 0) {
        return [`${p.farmaciasProcesadas + omitidas} / ${p.farmaciasAnalizadas} farmacias`, textoOmitidas].filter(Boolean).join(', ');
      }
      if (p.fase >= FASE_TOTALES) {
        const parts = [];
        if (p.totalTransmisiones > 0) parts.push(`${p.totalTransmisiones} transmisiones`);
        if (p.totalEmpleadosAE > 0) parts.push(`${p.totalEmpleadosAE} altas empl.`);
        if (p.totalEmpleadosCE > 0) parts.push(`${p.totalEmpleadosCE} cierres empl.`);
        if (textoOmitidas) parts.push(textoOmitidas);
        return parts.length > 0 ? parts.join(', ') : 'sin cambios';
      }
      return null;
    },
  },
  {
    key: 'traslados',
    label: 'Traslados',
    completedWhenFase: 8,
    getDetail: (p) => {
      if (p.fase < FASE_TOTALES) return null;
      return p.totalTraslados > 0 ? `${p.totalTraslados} detectados` : 'sin cambios';
    },
  },
  {
    key: 'escritura',
    label: 'Escritura en BBDD',
    completedWhenFase: 9,
  },
];

const FASE_NAMES: Record<number, string> = {
  0: 'Inicio',
  1: 'Lectura CU2',
  2: 'Lectura Gescom',
  3: 'Farmacias',
  4: 'Farmacéuticos',
  5: 'Relaciones',
  6: 'Traslados',
  8: 'Escritura',
  9: 'Completado',
};

// Lo que está haciendo el motor en cada fase, para «Escribiendo resultados… 12 min».
const FASE_EN_CURSO: Record<number, string> = {
  0: 'Iniciando',
  1: 'Leyendo CU2',
  2: 'Leyendo Gescom',
  3: 'Analizando farmacias',
  4: 'Analizando farmacéuticos',
  5: 'Analizando relaciones',
  6: 'Analizando traslados',
  8: 'Escribiendo resultados',
};

/**
 * Minutos enteros en la fase actual, desde FC_FASE. Las dos fechas son absolutas, así que la zona del navegador
 * no cuenta; la fase 8 puede pasar media hora sin que cambie nada más en la cabecera.
 */
export const minutosEnFase = (faseTimestamp: string | undefined, ahora: number): number | null => {
  if (!faseTimestamp) return null;
  const desde = Date.parse(faseTimestamp);
  return Number.isNaN(desde) ? null : Math.max(0, Math.floor((ahora - desde) / 60000));
};

const MotorProgressOverlay: React.FC<MotorProgressOverlayProps> = ({ visible, modo, opcion = 'titulares', onClose, onCompleted }) => {
  const [state, setState] = useState<OverlayState>('launching');
  const [progreso, setProgreso] = useState<ReconciliacionProgreso | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((recId: number) => {
    cleanup();
    setState('polling');
    const poll = async () => {
      if (!mountedRef.current) return;
      try {
        const p = await getProgreso(recId);
        if (!mountedRef.current) return;
        setProgreso(p);

        if (p.estado === 1) {
          cleanup();
          setState('completed');
        } else if (p.estado === 2) {
          cleanup();
          setState('error');
          setErrorMsg(p.errorDetalle || 'Error desconocido durante la ejecución');
        } else if (p.huerfano) {
          cleanup();
          setState('huerfano');
          setErrorMsg(MENSAJE_HUERFANO);
        }
      } catch {
        // Silently continue polling on transient errors
      }
    };

    // Primer poll inmediato
    poll();
    pollingRef.current = setInterval(poll, INTERVALO_SONDEO_MS);
  }, [cleanup]);

  // Tras el 202: busca el primer ciclo posterior al previo, cada 2 s y durante 120 s como mucho.
  const esperarCicloNuevo = useCallback((previoId: number) => {
    const limite = Date.now() + ESPERA_CICLO_NUEVO_MS;
    const buscar = async () => {
      if (!mountedRef.current) return;
      try {
        const nuevo = await getCicloNuevo(previoId);
        if (!mountedRef.current) return;
        if (nuevo) {
          startPolling(nuevo.id);
          return;
        }
      } catch { /* se reintenta en la siguiente vuelta */ }

      if (Date.now() >= limite) {
        cleanup();
        setState('launch_failed');
        setErrorMsg(MENSAJE_NO_ARRANCO);
      }
    };

    cleanup();
    buscar();
    pollingRef.current = setInterval(buscar, INTERVALO_SONDEO_MS);
  }, [cleanup, startPolling]);

  const launch = useCallback(async () => {
    cleanup();
    setState('launching');
    setErrorMsg('');
    setProgreso(null);

    try {
      const result = await lanzarMotor(modo, opcion);
      if (!mountedRef.current) return;
      if (!result.ok) {
        // El 409 de un ciclo huérfano no se arregla reintentando: lo cierra Sistemas.
        setState(result.error === MENSAJE_HUERFANO ? 'huerfano' : 'error');
        setErrorMsg(result.error || 'No se pudo lanzar el motor');
        return;
      }
      esperarCicloNuevo(result.previoId ?? 0);
    } catch (err: any) {
      setState('error');
      setErrorMsg(err.message || 'Error al comunicar con el servidor');
    }
  }, [modo, opcion, cleanup, esperarCicloNuevo]);

  useEffect(() => {
    mountedRef.current = true;
    if (visible) {
      launch();
    }
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [visible, launch, cleanup]);

  const handleClose = () => {
    cleanup();
    onClose();
    if (state === 'completed') {
      onCompleted();
    }
  };

  const handleRetry = () => {
    launch();
  };

  // La barra de la fase 5 cuenta también las omitidas: al terminar, procesadas + omitidas = analizadas (§4.3).
  const getProgressPercent = (): number => {
    if (!progreso || progreso.fase !== 5 || progreso.farmaciasAnalizadas === 0) return 0;
    const hechas = progreso.farmaciasProcesadas + (progreso.farmaciasOmitidas ?? 0);
    return Math.min(100, Math.round((hechas / progreso.farmaciasAnalizadas) * 100));
  };

  // Estado de cada fase de la UI: pendiente / en curso (la primera no completada) / hecha
  type EstadoFaseUI = 'pendiente' | 'en-curso' | 'hecha';
  const estadoFase = (faseUI: FaseUI): EstadoFaseUI => {
    if (!progreso) return 'pendiente';
    if (progreso.fase >= faseUI.completedWhenFase) return 'hecha';
    const prevFase = FASES_UI[FASES_UI.indexOf(faseUI) - 1];
    const isActive = !prevFase || progreso.fase >= prevFase.completedWhenFase;
    return isActive ? 'en-curso' : 'pendiente';
  };

  // Lista de fases con el patrón del manual (fct-fases): icono en una tinta, en curso en negrita, hecha en verde oscuro
  const ICONO_FASE = { pendiente: 'punto', 'en-curso': 'transmision', hecha: 'completado' } as const;
  const CLASE_FASE = { pendiente: undefined, 'en-curso': 'fct-en-curso', hecha: 'fct-hecha' } as const;

  const renderFases = () => (
    <ol className="fct-fases" style={{ padding: '8px 0' }}>
      {FASES_UI.map((faseUI) => {
        const detail = progreso && faseUI.getDetail ? faseUI.getDetail(progreso) : null;
        const isProgressPhase = faseUI.hasProgress && progreso?.fase === 5;
        const estado = estadoFase(faseUI);
        const pct = getProgressPercent();

        return (
          <li key={faseUI.key} className={CLASE_FASE[estado]} style={{ alignItems: 'flex-start' }}>
            <FctIcono nombre={ICONO_FASE[estado]} titulo={estado === 'hecha' ? 'Hecho' : undefined} />
            <div style={{ flex: 1 }}>
              <span>
                {faseUI.label}
                {detail && <span className="fct-pie" style={{ marginLeft: 12 }}>{detail}</span>}
              </span>
              {isProgressPhase && progreso!.farmaciasAnalizadas > 0 && (
                <div
                  className="fct-progreso"
                  style={{ marginTop: 4, maxWidth: 300, gridTemplateColumns: '1fr auto', alignItems: 'center' }}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                >
                  <div className="fct-progreso__pista"><i style={{ width: `${pct}%` }} /></div>
                  <span className="fct-mono" style={{ fontSize: 12 }}>{pct}%</span>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );

  // «Escribiendo resultados… 12 min»: para que una fase larga no parezca un cuelgue.
  const renderTiempoEnFase = () => {
    if (!progreso || progreso.estado !== 0) return null;
    const minutos = minutosEnFase(progreso.faseTimestamp, Date.now());
    const texto = FASE_EN_CURSO[progreso.fase];
    if (minutos === null || !texto) return null;
    return <Text type="secondary" className="fct-pie">{`${texto}… ${minutos} min`}</Text>;
  };

  const renderContent = () => {
    if (state === 'launching') {
      return (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <LoadingOutlined style={{ fontSize: 32 }} />
          <div style={{ marginTop: 12 }}>
            <Text>Lanzando motor de reconciliación...</Text>
          </div>
        </div>
      );
    }

    if (state === 'huerfano') {
      return (
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <FctAviso tipo="aviso" titulo={MENSAJE_HUERFANO} />
          </div>
          <div style={{ textAlign: 'right' }}>
            <Button onClick={handleClose}>Cerrar</Button>
          </div>
        </div>
      );
    }

    if (state === 'launch_failed' || state === 'error') {
      return (
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <FctAviso tipo="error" titulo="Error durante la reconciliación">
              {progreso && state === 'error' && (
                <span style={{ display: 'block', marginTop: 4 }}>Fase alcanzada: {FASE_NAMES[progreso.fase] || `Fase ${progreso.fase}`}</span>
              )}
              <span style={{ display: 'block', marginTop: 4 }}>{errorMsg}</span>
            </FctAviso>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={handleClose}>Cerrar</Button>
              <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetry}>Reintentar</Button>
            </Space>
          </div>
        </div>
      );
    }

    if (state === 'completed') {
      return (
        <div style={{ padding: '16px 0' }}>
          {renderFases()}
          {progreso && (
            <div style={{ marginTop: 16 }}>
              <FctAviso tipo="exito" titulo="Reconciliación completada">
                <span style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span>Operaciones: <span className="fct-mono">{progreso.totalOperaciones}</span></span>
                  <span>Excepciones: <span className="fct-mono">{progreso.totalExcepciones}</span></span>
                  <span>Invariantes: <span className="fct-mono">{progreso.totalInvariantes}</span></span>
                  <span>Farmacias omitidas: <span className="fct-mono">{progreso.farmaciasOmitidas ?? 0}</span></span>
                </span>
              </FctAviso>
            </div>
          )}
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button type="primary" onClick={handleClose}>Cerrar</Button>
          </div>
        </div>
      );
    }

    // state === 'polling'
    return (
      <div style={{ padding: '16px 0' }}>
        {renderFases()}
        {renderTiempoEnFase()}
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      closable={state === 'completed' || state === 'error' || state === 'launch_failed' || state === 'huerfano'}
      maskClosable={false}
      footer={null}
      onCancel={handleClose}
      title={<Title level={5} style={{ margin: 0 }}>Reconciliación en curso</Title>}
      width={520}
    >
      {renderContent()}
    </Modal>
  );
};

export default MotorProgressOverlay;
